import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Sparkles, Check, Gift, Copy } from "lucide-react";

const PROMO_CODE = "WELCOME10";

const NewsletterSection = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [acceptPromo, setAcceptPromo] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Basic validation
    if (!firstName.trim()) {
      setError("Veuillez entrer votre prénom");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Veuillez entrer une adresse email valide");
      return;
    }
    if (!acceptPromo) {
      setError("Veuillez accepter de recevoir nos communications");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: firstName.trim(),
            email: email.trim().toLowerCase(),
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        console.log("Newsletter subscription successful:", data);
        setSubmitted(true);
      } else {
        // Si l'email existe déjà, on montre quand même le code promo
        if (data.message.includes("déjà inscrite")) {
          setSubmitted(true);
        } else {
          setError(data.message || "Une erreur est survenue");
        }
      }
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      // En cas d'erreur réseau, on permet quand même l'affichage du code promo
      // pour ne pas bloquer l'expérience utilisateur
      console.log("Fallback to local mode due to network error");
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  if (submitted) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-soft" data-testid="newsletter-success">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
            Merci {firstName} !
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Voici votre code promo de bienvenue :
          </p>
          
          {/* Promo Code Display */}
          <div className="bg-primary/10 border-2 border-dashed border-primary/30 rounded-xl p-4 mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
              Code promo -10%
            </p>
            <div className="flex items-center justify-center gap-3">
              <span className="font-mono text-2xl font-bold text-primary tracking-wider">
                {PROMO_CODE}
              </span>
              <button
                onClick={handleCopyCode}
                className={`p-2 rounded-lg transition-all ${
                  codeCopied 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
                title="Copier le code"
              >
                {codeCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {codeCopied && (
              <p className="text-xs text-green-600 mt-2">Code copié !</p>
            )}
          </div>
          
          <p className="text-xs text-muted-foreground">
            Valable sur votre première commande sur lithae.com
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl border-2 border-primary/30 p-6 sm:p-8 shadow-soft overflow-hidden relative" data-testid="newsletter-section">
      {/* Promo Banner */}
      <div className="absolute -top-1 -right-12 rotate-45 bg-primary text-primary-foreground px-12 py-1 text-xs font-bold shadow-md">
        OFFRE
      </div>
      
      <div className="text-center mb-6">
        {/* Big -5% Badge */}
        <div className="inline-flex items-center justify-center bg-primary text-primary-foreground rounded-full w-20 h-20 mb-4 shadow-lg">
          <span className="text-2xl font-bold">-10%</span>
        </div>
        
        <h3 className="font-serif text-2xl font-bold text-foreground mb-2">
          Offre de bienvenue
        </h3>
        <p className="text-sm text-muted-foreground">
          Inscrivez-vous et recevez <span className="font-semibold text-primary">votre code promo</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="text"
            placeholder="Votre prénom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full bg-background border-border focus:border-primary focus:ring-primary/20"
            data-testid="newsletter-firstname"
          />
        </div>

        <div>
          <Input
            type="email"
            placeholder="Votre adresse email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-background border-border focus:border-primary focus:ring-primary/20"
            data-testid="newsletter-email"
          />
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="acceptPromo"
            checked={acceptPromo}
            onCheckedChange={(checked) => setAcceptPromo(checked)}
            className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            data-testid="newsletter-checkbox"
          />
          <label
            htmlFor="acceptPromo"
            className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
          >
            J'accepte de recevoir les newsletters, offres promotionnelles et conseils personnalisés de Lithae
          </label>
        </div>

        {error && (
          <p className="text-sm text-red-500 text-center" data-testid="newsletter-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          className="w-full flex items-center justify-center gap-2"
          data-testid="newsletter-submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
              Inscription...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Recevoir mon code -10%
            </>
          )}
        </Button>
      </form>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Vos données sont protégées. Désabonnement possible à tout moment.
      </p>
    </div>
  );
};

export default NewsletterSection;
